import { Button, DismissibleAlert, useDismissableOverlay } from '@catering/ui';
import { Link, Navigate, Outlet, useNavigate } from '@tanstack/react-router';
import { Menu, X } from 'lucide-react';
import { type JSX, useEffect, useState } from 'react';

import { apiRequest } from '../api/client';
import { useSessionStore } from '../auth/session-store';
import { useCurrentUser } from '../auth/use-current-user';
import { useLogout } from '../auth/use-logout';
import { NotificationBell } from './notification-bell';
import { SidebarNav } from './sidebar-nav';
import { UserMenu } from './user-menu';
import { useIsDesktop } from './use-is-desktop';

export function AppShell(): JSX.Element {
  const currentUser = useCurrentUser();
  const isRestoring = useSessionStore((state) => state.isRestoring);
  const isAuthenticated = useSessionStore((state) => state.isAuthenticated());
  const logout = useLogout();
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { containerRef: drawerRef } = useDismissableOverlay({
    open: drawerOpen,
    onClose: () => setDrawerOpen(false),
  });

  const isCustomerAccount = currentUser.data?.isInternal === false;
  const isReady = currentUser.data?.isInternal === true;

  /**
   * A customer-portal account carries a perfectly valid session — the JWT
   * says who they are, the same as any staff member's — but `isInternal` on
   * `GET /auth/me` is the one signal the client has that they used the
   * wrong app. Signing out here, rather than merely hiding the shell, closes
   * the server-side session too: `logout` clears the refresh cookie, so a
   * reload cannot land back in the back-office by accident.
   */
  useEffect(() => {
    if (!isCustomerAccount) {
      return;
    }

    void apiRequest<void>('/auth/logout', { method: 'POST', body: JSON.stringify({}) })
      .catch(() => undefined)
      .then(() => {
        useSessionStore
          .getState()
          .signOutWithError('Esta cuenta es de cliente. Ingresá desde el portal.');
        void navigate({ to: '/login' });
      });
  }, [isCustomerAccount, navigate]);

  async function handleLogout(): Promise<void> {
    await logout.mutateAsync();
    await navigate({ to: '/login' });
  }

  /**
   * FINDINGS.md → segunda corrida: `GET /auth/me` still being in flight is
   * its own state, distinct from "internal" and "not internal" — rendering
   * the shell (or anything from it) before that call resolves is exactly
   * the window a non-internal account used to see the dashboard in. Nothing
   * from the protected app — no sidebar, no header, no `<Outlet />` — may
   * render until the caller is confirmed internal.
   */
  // The route guard lets the shell mount while the silent restore is still
  // running. When that restore fails (a stale or rotated refresh cookie), or
  // the session is cleared later on, nothing re-runs the guard: the shell
  // itself has to send the caller to the login screen.
  if (!isRestoring && !isAuthenticated) {
    return <Navigate to="/login" />;
  }

  // Only when there is nothing to show: a failed background refetch keeps the
  // last known profile on screen.
  if (currentUser.isError && currentUser.data === undefined) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-slate-100">
        <p role="alert" className="text-sm text-slate-700">
          No pudimos cargar tu sesión.
        </p>
        <Button variant="secondary" onClick={() => void currentUser.refetch()}>
          Reintentar
        </Button>
      </div>
    );
  }

  if (!isReady) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-100">
        <p role="status" className="text-sm text-slate-500">
          Cargando...
        </p>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-slate-100">
      {/* Landmarks, not just boxes: they are how somebody using a screen
          reader skips straight to the content instead of hearing the menu
          again on every page. */}
      {/* Sticky and screen-tall: the white column reaches the bottom of the
          viewport on short pages and stays in place on long ones. */}
      {isDesktop ? (
        <div className="sticky top-0 h-screen shrink-0">
          <SidebarNav currentUser={currentUser.data} />
        </div>
      ) : null}

      {!isDesktop && drawerOpen ? (
        <>
          <div
            role="presentation"
            className="fixed inset-0 z-30 bg-slate-900/40"
            onClick={() => setDrawerOpen(false)}
          />
          <div ref={drawerRef} className="fixed inset-y-0 left-0 z-40">
            <button
              type="button"
              aria-label="Cerrar menú"
              onClick={() => setDrawerOpen(false)}
              className="absolute right-2 top-2 z-10 rounded-md p-2 text-slate-600 transition-colors hover:bg-slate-100"
            >
              <X aria-hidden="true" className="size-5" />
            </button>
            <SidebarNav currentUser={currentUser.data} onNavigate={() => setDrawerOpen(false)} />
          </div>
        </>
      ) : null}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex items-center justify-between gap-4 border-b border-slate-200 bg-white px-6 py-3">
          {!isDesktop ? (
            <button
              type="button"
              aria-label="Abrir menú de navegación"
              onClick={() => setDrawerOpen(true)}
              className="rounded-md p-2 text-slate-600 transition-colors hover:bg-slate-100"
            >
              <Menu aria-hidden="true" className="size-5" />
            </button>
          ) : (
            <span />
          )}

          {currentUser.data !== undefined ? (
            <div className="flex items-center gap-2">
              <NotificationBell />
              <UserMenu
                fullName={currentUser.data.fullName}
                email={currentUser.data.email}
                onLogout={() => void handleLogout()}
              />
            </div>
          ) : null}
        </header>

        <main className="flex-1 bg-slate-50 p-6">
          {currentUser.data?.mustEnrollTotp === true ? (
            <DismissibleAlert
              storageKey={`2fa-banner-dismissed:${currentUser.data.id}`}
              tone="warning"
              className="mb-6"
            >
              Tu rol exige verificación en dos pasos.{' '}
              <Link to="/account/security" className="font-medium underline">
                Configurar verificación
              </Link>
              .
            </DismissibleAlert>
          ) : null}

          <Outlet />
        </main>
      </div>
    </div>
  );
}
